import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { Scene } from '@babylonjs/core/scene';
import type { Nullable } from '@babylonjs/core/types';
import type { IScene, ISceneEntity } from '@seddi/scene';
import type { FileLoader } from '@seddi/ui-babylon-core';
import log from 'loglevel';
import type { ButtonsContext } from '../resources/buttonsContext';
import type { FabricsContext } from '../resources/fabricsContext';
import type { LogosContext } from '../resources/logosContext';
import type { SurfaceMaterialLoader } from '../resources/surfaceMaterialLoader';
import { EntityRenderable } from '../renderables/entityRenderable';
import { EcGarmentGeometryLoader } from './ecGarmentGeometryLoader';
import { EcMeshLoader } from './ecMeshLoader';
import { EcMeshRenderableLoader } from './ecMeshRenderableLoader';
import { EcPieceLoader } from './ecPieceLoader';
import { EcSeamLoader } from './ecSeamLoader';
import { StitchLoader } from './stitchLoader';

/**
 * Loads a scene entity.
 */
export class EntityLoader {
  /** Babylon scene. */
  private readonly babylonScene: Scene;
  /** Stitch loader. */
  private readonly stitchLoader: StitchLoader;
  /** Buttons context. */
  protected readonly buttons: ButtonsContext;
  /** Fabrics context. */
  protected readonly fabrics: FabricsContext;
  /** Logos context. */
  protected readonly logos: LogosContext;

  private readonly garmentGeometryLoader: EcGarmentGeometryLoader;
  private readonly meshLoader: EcMeshLoader;
  private readonly meshRenLoader: EcMeshRenderableLoader;
  private readonly pieceLoader: EcPieceLoader;
  private readonly seamLoader: EcSeamLoader;

  private _assetContainer: Nullable<AssetContainer> = null;

  constructor(
    scene: Scene,
    fileLoader: FileLoader,
    surfaceMaterialLoader: SurfaceMaterialLoader,
    buttons: ButtonsContext,
    fabrics: FabricsContext,
    logos: LogosContext,
  ) {
    this.babylonScene = scene;
    this.buttons = buttons;
    this.fabrics = fabrics;
    this.logos = logos;

    this.stitchLoader = new StitchLoader(scene);
    this.garmentGeometryLoader = new EcGarmentGeometryLoader(fileLoader);
    this.meshLoader = new EcMeshLoader(this.babylonScene);
    this.meshRenLoader = new EcMeshRenderableLoader(surfaceMaterialLoader);
    this.pieceLoader = new EcPieceLoader(
      this.babylonScene,
      this.stitchLoader,
      this.buttons,
      this.fabrics,
      this.logos,
    );
    this.seamLoader = new EcSeamLoader(this.babylonScene, this.stitchLoader);
  }

  /**
   * Loads a scene entity.
   * @param scene - Scene parent.
   * @param entity - Entity to load.
   */
  async loadAsync(scene: IScene, entity: ISceneEntity): Promise<EntityRenderable> {
    return this._loadAsync(scene, entity);
  }

  /**
   * Loads a scene entity.
   * @param scene - Scene to load.
   * @param sceneContext - Fx scene context.
   */
  async loadAssetContainerAsync(
    scene: IScene,
    entity: ISceneEntity,
    container: Nullable<AssetContainer>,
  ): Promise<EntityRenderable> {
    return this._loadAsync(scene, entity, container);
  }

  /**
   * Loads a scene entity.
   * @param scene - Scene to load.
   * @param sceneContext - Fx scene context.
   */
  async _loadAsync(
    sceneData: IScene,
    data: ISceneEntity,
    container: Nullable<AssetContainer> = null,
  ): Promise<EntityRenderable> {
    log.debug(`entity ${data._id} loading`);
    this._assetContainer = container;

    const { garmentGeometry, garmentPiece, garmentSeam, mesh, meshRenderable, transform3D } =
      data.components;

    // Create renderable.
    this.babylonScene._blockEntityCollection = Boolean(this._assetContainer);
    const renderable = new EntityRenderable(data._id, this.babylonScene);
    this.babylonScene._blockEntityCollection = false;

    // Load transform.
    if (transform3D) {
      renderable.updateTransform(transform3D);
    }

    // Load components.
    if (garmentGeometry) {
      await this.garmentGeometryLoader.loadAsync(sceneData, garmentGeometry);
    }
    if (garmentPiece) {
      const piece = await this.pieceLoader.loadAssetContainerAsync(
        sceneData,
        data,
        renderable.node,
        container,
      );
      renderable.piece = piece;
    }
    if (garmentSeam) {
      const seam = await this.seamLoader.loadAssetContainerAsync(
        sceneData,
        data,
        renderable.node,
        container,
      );
      renderable.seam = seam;
    }
    if (mesh) {
      await this.meshLoader.loadAssetContainerAsync(sceneData, data, renderable.node, container);
    }
    if (meshRenderable) {
      await this.meshRenLoader.loadAssetContainerAsync(sceneData, data, renderable.node, container);
    }

    return renderable;
  }

  /**
   * Unloads a scene entity.
   * @param scene - Scene to load.
   * @param sceneContext - Fx scene context.
   */
  unload(entity: EntityRenderable): Promise<void> {
    entity.node.dispose();
    return Promise.resolve();
  }
}
